import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function PhysicianAssistantsPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d62j4380h9ov63emh8dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <div className="inline-block px-4 py-2 bg-[#7C3AED]/10 rounded-full text-[#7C3AED] text-sm font-medium mb-6">
                Physician Assistants in Hormone & Wellness Medicine
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Grow Your PA Practice with Qualified Wellness Patients
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                HIPAA-compliant digital marketing and automated patient acquisition for physician assistants specializing in hormone therapy, wellness medicine, and preventive care.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#contact-form" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap">
                  Book Free Strategy Call
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <Link to="/services/marketing-automation" className="inline-flex items-center px-8 py-4 bg-white text-[#7C3AED] rounded-lg font-semibold hover:bg-[#7C3AED]/5 transition-all border-2 border-[#7C3AED] whitespace-nowrap">
                  See Our Automation Platform
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/ac88960d53d6c7bea4b3968502350f5d.jpg"
                alt="Physician Assistant Practice"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Marketing Challenges for Physician Assistants
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique obstacles PAs face when building their practice
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Establishing Authority</h3>
              <p className="text-gray-600">
                Building credibility and positioning yourself as an expert in hormone therapy and wellness medicine.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Low Online Visibility</h3>
              <p className="text-gray-600">
                Patients searching for hormone therapy, wellness care, or preventive medicine can't find your PA practice.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Competition with Physicians</h3>
              <p className="text-gray-600">
                Standing out in a market where patients often default to searching for doctors rather than PAs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-time-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No Time for Marketing</h3>
              <p className="text-gray-600">
                Busy clinical schedules leave no time for social media, content creation, or lead follow-up.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-phone-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Missed Patient Inquiries</h3>
              <p className="text-gray-600">
                Leads go unanswered when you're with patients, resulting in lost appointments and revenue.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">HIPAA Compliance</h3>
              <p className="text-gray-600">
                Ensuring all marketing tools and patient communications meet healthcare privacy requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our HIPAA-Compliant Marketing Solutions for PAs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven strategies designed specifically for physician assistant practices
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20physician%20assistant%20in%20medical%20attire%20consulting%20with%20patient%20about%20wellness%20and%20hormone%20therapy%2C%20modern%20healthcare%20clinic%2C%20compassionate%20medical%20provider%2C%20patient%20consultation%2C%20preventive%20medicine%20approach&width=600&height=400&seq=pa1&orientation=landscape"
                alt="Physician assistant consultation"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Automated Patient Acquisition System
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Our marketing automation platform captures, nurtures, and converts leads while you focus on patient care.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">Instant Lead Response:</strong> Automated follow-up within minutes of every inquiry</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">Patient Education:</strong> Automated content showcasing your expertise and services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">Seamless Booking:</strong> Automated scheduling that fills your calendar</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-purple-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong className="text-gray-900">HIPAA-Conscious:</strong> Secure systems designed for healthcare compliance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Marketing Services for Your PA Practice
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to attract and convert more patients
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Link to="/services/medical-seo" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Medical SEO</h3>
              <p className="text-gray-600 mb-4">
                Rank #1 for "physician assistant near me," "hormone therapy PA," and wellness medicine searches.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/medical-website-design" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-layout-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Website Design</h3>
              <p className="text-gray-600 mb-4">
                Professional websites that establish your authority and drive appointment bookings.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/content-marketing" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-article-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Content Marketing</h3>
              <p className="text-gray-600 mb-4">
                Educational content that positions you as an expert in hormone therapy and wellness medicine.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/healthcare-ppc" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-advertisement-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Google & Meta Ads</h3>
              <p className="text-gray-600 mb-4">
                HIPAA-compliant paid advertising that brings qualified patients actively seeking your services.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/marketing-automation" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Marketing Automation</h3>
              <p className="text-gray-600 mb-4">
                Never miss a lead with automated nurturing, follow-up, and patient engagement systems.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>

            <Link to="/services/reputation-management" className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Reputation Management</h3>
              <p className="text-gray-600 mb-4">
                Build trust and credibility with more 5-star reviews from satisfied patients.
              </p>
              <span className="text-purple-600 font-semibold group-hover:underline">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Successful Physician Assistants
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-8 rounded-xl border border-purple-100">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20male%20physician%20assistant%20headshot%20portrait%2C%20wellness%20medicine%20specialist%2C%20confident%20healthcare%20provider%2C%20medical%20professional&width=80&height=80&seq=patest1&orientation=squarish"
                  alt="James Wilson"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <div className="font-bold text-gray-900">James Wilson, PA-C</div>
                  <div className="text-sm text-gray-600">Hormone Therapy & Wellness Medicine</div>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed">
                "MillionDollarMed helped me establish my PA practice as a leader in hormone therapy. Their automation platform ensures I never miss a lead, and my patient volume has increased 175% in 7 months. Best investment I've made!"
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-8 rounded-xl border border-purple-100">
              <div className="flex items-center mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20female%20physician%20assistant%20headshot%20portrait%2C%20hormone%20therapy%20specialist%2C%20experienced%20healthcare%20provider%2C%20confident%20medical%20professional&width=80&height=80&seq=patest2&orientation=squarish"
                  alt="Emily Davis"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <div className="font-bold text-gray-900">Emily Davis, PA-C</div>
                  <div className="text-sm text-gray-600">Preventive & Wellness Medicine</div>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed">
                "Finally found a marketing partner who understands PA practices. Their SEO strategy has me ranking #1 for wellness medicine in my area, and the automated follow-up converts leads while I'm seeing patients. Highly recommend!"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Free Strategy Call
            </h2>
            <p className="text-xl text-gray-600">
              Let's discuss how we can help grow your PA practice with HIPAA-compliant marketing and automation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl shadow-lg" data-readdy-form id="pa-strategy-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Your Name, PA-C"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="pa@practice.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label htmlFor="practice" className="block text-sm font-semibold text-gray-700 mb-2">
                  Practice Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="practice"
                  name="practice"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Your Practice Name"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="specialty" className="block text-sm font-semibold text-gray-700 mb-2">
                Specialty Focus <span className="text-red-500">*</span>
              </label>
              <select
                id="specialty"
                name="specialty"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                <option value="">Select your specialty</option>
                <option value="hormone-therapy">Hormone Therapy</option>
                <option value="wellness-medicine">Wellness Medicine</option>
                <option value="preventive-care">Preventive Care</option>
                <option value="family-practice">Family Practice</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                What are your biggest marketing challenges? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell us about your practice goals and challenges..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>

            <div className="mb-6">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="consent"
                  required
                  className="mt-1 mr-3"
                />
                <span className="text-sm text-gray-600">
                  I agree to receive communications from MillionDollarMed. All information is handled in accordance with HIPAA guidelines.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-violet-600 text-white py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-violet-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book My Free Strategy Call
            </button>

            {formStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-center font-semibold">
                  Thank you! We'll contact you within 24 hours to schedule your strategy call.
                </p>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-center font-semibold">
                  Something went wrong. Please try again or call us directly.
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your PA Practice?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join successful physician assistants who trust MillionDollarMed for HIPAA-compliant marketing and automated patient acquisition.
          </p>
          <a href="#contact-form" className="inline-flex items-center px-8 py-4 bg-white text-[#0D9488] rounded-lg font-semibold hover:bg-gray-50 transition-all text-lg whitespace-nowrap">
            Schedule Your Free Consultation
            <i className="ri-calendar-line ml-2"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
